import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  Input,
  Badge,
  Button,
  UncontrolledTooltip,
} from "reactstrap";
import Select, { components } from "react-select";
import { useNavigate } from "react-router-dom";
import { urlEncodedId } from "components/CommonComponents/encodeDecodeFunction";
import { DEFAULT_RECORDS_PER_PAGE } from "components/constant";
import { fixedWidthCustomStyle } from "constants/CommonConstants";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import { useDispatch, useSelector } from "react-redux";
import { getEnrolledTraineesListAgainstCourse } from "action/course";
// NOTE: verify this constant actually exists in constants/course — it's
// guessed to match the selector key below (same convention as
// channelPartnerEnrolledCourseList in ChannelPartnerMyCourses.jsx).
import { enrolledChannelPartnerListAgainstCourse } from "constants/course";

// TODO: fill in the real set of enrollmentStatus values your backend returns
// (only "ENROLLED" shows up in the sample response) and adjust the color
// mapping in ENROLLMENT_STATUS_META below to match.
const ENROLLMENT_STATUS_OPTIONS = [
  { value: "ENROLLED", label: "Enrolled" },
];

const ENROLLMENT_STATUS_META = {
  ENROLLED: { color: "success" },
  CANCELLED: { color: "danger" },
  COMPLETED: { color: "info" },
};

const EnrolledTraineesTab = ({ courseId }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const traineeDataList = useSelector(
    (state) => state.course.enrolledChannelPartnerListAgainstCourse,
  );

  const [pageNum, setPageNum] = useState(1);
  const [filterState, setFilterState] = useState({
    searchName: "",
    searchEmail: "",
    searchMobile: "",
    searchStatus: null,
    filterParams: [],
  });

  const [alternative, setAlternative] = useState({
    isLoading: true,
    showLoaderOnClick: false,
    error: "",
    noRecords: false,
  });

  const [isRemoving, setIsRemoving] = useState(false);

  const buildFilterParams = () => {
    const filterParams = [];

    if (filterState.searchName.trim()) {
      filterParams.push({
        field: "Name",
        condition: "like",
        filterValues: [filterState.searchName.trim()],
      });
    }

    if (filterState.searchEmail.trim()) {
      filterParams.push({
        field: "Email",
        condition: "like",
        filterValues: [filterState.searchEmail.trim()],
      });
    }

    if (filterState.searchMobile.trim()) {
      filterParams.push({
        field: "Mobile",
        condition: "like",
        filterValues: [filterState.searchMobile.trim()],
      });
    }

    if (filterState.searchStatus) {
      // NOTE: confirm "EnrollmentStatus" is the field name the backend
      // actually expects for this filter.
      filterParams.push({
        field: "EnrollmentStatus",
        condition: "eq",
        filterValues: [filterState.searchStatus.value],
      });
    }

    return {
      filters: filterParams.length > 0 ? JSON.stringify(filterParams) : [],
    };
  };

  const formattedTraineeList = traineeDataList?.data?.trainees?.map(
    (item, index) => {
      return {
        ...item,
        srNo:
          (pageNum - 1) *
          (traineeDataList?.data?.noOfRecordsPerPage ||
            DEFAULT_RECORDS_PER_PAGE) +
          index +
          1,
        fullName:
          item?.name || `${item?.firstName || ""} ${item?.lastName || ""}`.trim(),
        email: item?.email || "",
        mobileNo: item?.mobile || "",
        batch: item?.batchName || "-",
        batchCode: item?.batchCode || "-",
        status: item?.enrollmentStatus || "-",
        enrolledOn: item?.enrolledAt || "-",
      };
    },
  );

  useEffect(() => {
    setAlternative((prev) => ({
      ...prev,
      isLoading: true,
    }));

    getEnrolledTraineesListAgainstCourse(
      courseId,
      dispatch,
      pageNum,
      buildFilterParams(),
      filterState.searchName,
      {
        sortBy: "CreatedAt",
        sortType: "desc",
      },
    );

    return () => {
      dispatch({ type: enrolledChannelPartnerListAgainstCourse, payload: {} });
    };
  }, [dispatch, courseId, pageNum, filterState.filterParams]);

  useEffect(() => {
    if (traineeDataList?.code === 200) {
      setAlternative({
        isLoading: false,
        error: "",
        noRecords: traineeDataList?.data?.trainees?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (traineeDataList?.code || traineeDataList?.message) {
      setAlternative({
        isLoading: false,
        error:
          traineeDataList?.message ||
          "Failed to load enrolled trainees! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [traineeDataList]);

  const handleSearch = () => {
    if (
      !filterState.searchName.trim() &&
      !filterState.searchEmail.trim() &&
      !filterState.searchMobile.trim() &&
      !filterState.searchStatus
    ) {
      return;
    }

    const newFilterParams = buildFilterParams();
    if (
      JSON.stringify(filterState.filterParams) !== JSON.stringify(newFilterParams)
    ) {
      setAlternative((prev) => ({
        ...prev,
        isLoading: true,
        showLoaderOnClick: true,
      }));
      setFilterState((prev) => ({
        ...prev,
        filterParams: newFilterParams,
      }));
      if (pageNum !== 1) setPageNum(1);
    }
  };

  const clearSearch = () => {
    setAlternative((prev) => ({
      ...prev,
      isLoading: true,
    }));
    setFilterState({
      searchName: "",
      searchEmail: "",
      searchMobile: "",
      searchStatus: null,
      filterParams: [],
    });
    if (pageNum !== 1) setPageNum(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  // const handleRemove = (trainee) => {
  //   showAlert({
  //     title: "Remove trainee from this course?",
  //     message: `"${trainee.fullName}" will be unenrolled from this course. This action cannot be undone.`,
  //     type: "warning",
  //     confirmStyle: "deep-blue",
  //     cancelStyle: "secondary",
  //     confirmText: "Remove",
  //     cancelText: "Cancel",
  //     onConfirm: async (closeAlert) => {
  //       closeAlert();
  //       try {
  //         setIsRemoving(true);

  //         // TODO(API swap-point): call the real "remove enrolled trainee"
  //         // endpoint here once it's available, e.g.:
  //         // const res = await removeEnrolledTrainee(trainee.enrollmentID);
  //         await new Promise((resolve) => setTimeout(resolve, 500));

  //         notify("success", "Trainee removed from course");

  //         // Re-pull the list so pagination/counts stay accurate.
  //         getEnrolledTraineesListAgainstCourse(
  //           courseId,
  //           dispatch,
  //           pageNum,
  //           buildFilterParams(),
  //           filterState.searchName,
  //           { sortBy: "CreatedAt", sortType: "desc" },
  //         );
  //       } catch (error) {
  //         notify("danger", error?.message || "Something went wrong");
  //       } finally {
  //         setIsRemoving(false);
  //       }
  //     },
  //   });
  // };

  const Option = (props) => (
    <components.Option {...props}>
      <Badge color={ENROLLMENT_STATUS_META[props.data.value]?.color || "secondary"} pill>
        {props.data.label}
      </Badge>
    </components.Option>
  );

  const SingleValue = (props) => (
    <components.SingleValue {...props}>
      <Badge color={ENROLLMENT_STATUS_META[props.data.value]?.color || "secondary"} pill>
        {props.data.label}
      </Badge>
    </components.SingleValue>
  );

  const columns = [
    {
      Header: "Sr. No.",
      accessor: "srNo",
      minWidth: 60,
      maxWidth: 70,
      centerAlign: true,
    },
    {
      Header: "Name",
      accessor: "fullName",
      minWidth: 160,
      maxWidth: 250,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
    {
      Header: "Email",
      accessor: "email",
      minWidth: 200,
      maxWidth: 250,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
    {
      Header: "Mobile No.",
      accessor: "mobileNo",
      centerAlign: true,
      Cell: ({ value }) => <span>{value || "-"}</span>,
    },
    {
      Header: "Batch",
      accessor: "batch",
      minWidth: 200,
      maxWidth: 280,
      Cell: ({ row }) => (
        <div>
          <span className="text-wrapper d-block">{row.original.batch}</span>
          {row.original.batchCode !== "-" && (
            <span className="text-muted small">{row.original.batchCode}</span>
          )}
        </div>
      ),
    },
    { Header: "Enrolled On", accessor: "enrolledOn" },
    {
      Header: "Actions",
      disableSortBy: true,
      Cell: ({ row }) => {
        const encodedId = urlEncodedId(row.original.traineeID);
        return (
          <div className="d-flex">
            <Button
              className="btn-link d-flex align-items-center p-0"
              id={`view${row.original.enrollmentID}`}
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/channel-partner/trainee/${encodedId}/details`);
              }}
            >
              <i className="fas fa-eye mr-1 table-action-primary" />
            </Button>
            <UncontrolledTooltip delay={0} target={`view${row.original.enrollmentID}`}>
              View
            </UncontrolledTooltip>

            {/* <span className="mr-1 ml-1 text-muted">|</span> */}

            {/* <Button
              className="btn-link d-flex align-items-center p-0 ml-1"
              id={`remove${row.original.enrollmentID}`}
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleRemove(row.original);
              }}
            >
              <i className="fa fa-user-minus text-danger" />
            </Button>
            <UncontrolledTooltip delay={0} target={`remove${row.original.enrollmentID}`}>
              Remove from course
            </UncontrolledTooltip> */}
          </div>
        );
      },
    },
  ];

  return (
    <Card className="content-card">
      {isRemoving && (
        <div className="logout-overlay">
          <div className="logout-card">
            <i className="fa fa-spinner fa-spin fa-2x text-deep-blue mb-3" />
            <div className="font-weight-bold text-dark">
              Removing trainee… Please wait.
            </div>
          </div>
        </div>
      )}
      <CardBody>
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap flex-column flex-md-row">
          <div className="d-flex flex-wrap align-items-center filter-group">
            <Input
              type="text"
              placeholder="Search by name"
              className="mr-2 mb-2 filter-input-large"
              value={filterState.searchName}
              onChange={(e) =>
                setFilterState((prev) => ({ ...prev, searchName: e.target.value }))
              }
              onKeyDown={handleKeyDown}
            />
            <Input
              type="text"
              placeholder="Search by email"
              className="mr-2 mb-2 filter-input-large"
              value={filterState.searchEmail}
              onChange={(e) =>
                setFilterState((prev) => ({ ...prev, searchEmail: e.target.value }))
              }
              onKeyDown={handleKeyDown}
            />
            <Input
              type="text"
              placeholder="Search by mobile no."
              className="mr-2 mb-2 filter-input-large"
              value={filterState.searchMobile}
              onChange={(e) =>
                setFilterState((prev) => ({ ...prev, searchMobile: e.target.value }))
              }
              onKeyDown={handleKeyDown}
            />
           

            <Button
              color="default"
              className={`d-flex align-items-center mb-2 filter-btn btn-deep-blue ${alternative.showLoaderOnClick ? "search-btn" : ""}`}
              onClick={handleSearch}
            >
              {alternative.showLoaderOnClick ? (
                <i className="fa fa-spinner fa-spin loader" />
              ) : (
                <>
                  <i className="fa fa-search mr-1" />
                  Search
                </>
              )}
            </Button>

            {filterState.filterParams?.filters?.length > 0 && (
              <Button
                color="default"
                className="d-flex align-items-start mb-2 ml-0 filter-btn btn-deep-blue px-4"
                onClick={clearSearch}
              >
                <i className="fa fa-times mr-1 mt-1" /> Clear
              </Button>
            )}
          </div>
        </div>

        <div className="mt-2 mb-3 mx-1">
          {traineeDataList?.data?.totalRecords ? (
            <>
              {filterState.filterParams?.filters?.length > 0 ? (
                <span className="total-records">
                  Showing {traineeDataList?.data?.totalFilteredRecords} of{" "}
                  {traineeDataList?.data?.totalRecords} Records
                </span>
              ) : (
                <span className="total-records">
                  Total Records : {traineeDataList?.data?.totalRecords}
                </span>
              )}
            </>
          ) : null}
        </div>

        {alternative.isLoading ? (
          <div className="loaderContainer">
            <i className="fa fa-spinner fa-spin fa-2xl loader" />
          </div>
        ) : alternative.error ? (
          <div className="loaderContainer error-style">
            <div className="error-icon">
              <i className="fa-solid fa-circle-exclamation"></i>
            </div>
            <span>{alternative.error}</span>
          </div>
        ) : alternative.noRecords ? (
          <div className="loaderContainer empty-style">
            <div className="empty-icon">
              <i className="fa-solid fa-circle-info"></i>
            </div>
            <span>No trainees enrolled in this course yet.</span>
          </div>
        ) : (
          <div className="table-responsive">
            <CommonTable
              columns={columns}
              data={formattedTraineeList || []}
              defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
              activePage={pageNum}
              setPageNum={setPageNum}
              totalPages={
                Math.ceil(
                  (traineeDataList?.data?.totalFilteredRecords || 0) /
                  (traineeDataList?.data?.noOfRecordsPerPage || DEFAULT_RECORDS_PER_PAGE),
                ) || 1
              }
              showPaginationBottom={true}
            />
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default EnrolledTraineesTab;